/**
 * A composable that provides timing-related utility functions.
 *
 * @example
 * // Basic usage
 * const { debounce } = useTimer();
 *
 * // Debouncing a search function
 * const debouncedSearch = debounce((query) => {
 *   console.log('Searching for:', query);
 * }, 500);
 *
 * // Usage in an event handler
 * searchInput.addEventListener('input', (e) => {
 *   debouncedSearch(e.target.value);
 * });
 *
 * @returns {Object} An object containing timing utility functions
 */
export const useTimer = () => {
    /**
     * Creates a debounced version of a function that delays its execution until after
     * a specified time has elapsed since the last time it was invoked.
     *
     * @param {Function} func - The function to debounce
     * @param {number} [timeout=300] - The delay in milliseconds
     * @returns {Function} A debounced version of the provided function
     *
     * @example
     * const { debounce } = useTimer();
     *
     * // Debounce with default timeout (300ms)
     * const debouncedFn = debounce(() => {
     *   console.log('Executed after delay');
     * });
     *
     * // Debounce with custom timeout
     * const debouncedFn = debounce(() => {
     *   console.log('Executed after 1 second');
     * }, 1000);
     */
    const debounce = (func, timeout = 300) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, timeout);
        };
    };

    return {
        debounce,
    };
};
